import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import { Close, SupportAgent, OpenInNew } from '@mui/icons-material'
import { api } from '../../services/Main/Api'
import { getCondoId } from '../../services/Main/AuthStorage'
import moment from 'moment'

const CHECK_INTERVAL = 15 * 60 * 1000 // 15 minutos
const DISMISS_KEY = 'ticket_alert_dismissed_at'

const TicketPendingAlert = () => {
  const [open, setOpen] = useState(false)
  const [pendingTickets, setPendingTickets] = useState([])

  const checkPendingTickets = useCallback(() => {
    const condoId = getCondoId()
    if (!condoId) return

    // Verificar se foi dispensado recentemente
    const dismissedAt = localStorage.getItem(DISMISS_KEY)
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10)
      if (elapsed < CHECK_INTERVAL) return
    }

    api.get('tickets/', {
      params: {
        condo_id: condoId,
        client_needs_attention: true,
      }
    })
      .then(res => {
        const tickets = res.data.results || res.data
        if (tickets.length > 0) {
          setPendingTickets(tickets)
          setOpen(true)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const initialTimer = setTimeout(checkPendingTickets, 10000)
    const interval = setInterval(checkPendingTickets, CHECK_INTERVAL)
    return () => {
      clearTimeout(initialTimer)
      clearInterval(interval)
    }
  }, [checkPendingTickets])

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString())
    setOpen(false)
  }

  const handleOpenTicket = (ticketId) => {
    window.open(`/suporte/${ticketId}`, '_blank')
  }

  if (!pendingTickets.length) return null

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' }
      }}
    >
      {/* Header accent — slate gradient */}
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #334155 0%, #64748B 100%)' }} />

      <DialogContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2, bgcolor: '#F1F5F9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SupportAgent sx={{ fontSize: 24, color: '#475569' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>
                Tickets aguardando sua ação
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {pendingTickets.length === 1
                  ? 'Você tem 1 ticket respondido pela equipe de suporte'
                  : `Você tem ${pendingTickets.length} tickets respondidos pela equipe de suporte`
                }
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleDismiss}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box display="flex" flexDirection="column" gap={1.5}>
          {pendingTickets.slice(0, 5).map(ticket => (
            <Box
              key={ticket.id}
              sx={{
                p: 2, borderRadius: 2, border: '1px solid #E2E8F0',
                bgcolor: '#F8FAFC', cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#475569', bgcolor: '#F1F5F9' },
              }}
              onClick={() => handleOpenTicket(ticket.id)}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box flex={1} mr={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#475569' }}>
                      {ticket.protocol}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {moment(ticket.last_activity_at || ticket.created_at).fromNow()}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                    {ticket.title}
                  </Typography>
                </Box>
                <OpenInNew sx={{ fontSize: 16, color: '#94A3B8', flexShrink: 0 }} />
              </Box>
            </Box>
          ))}

          {pendingTickets.length > 5 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              e mais {pendingTickets.length - 5} ticket(s)...
            </Typography>
          )}
        </Box>

        <Typography variant="caption" sx={{ display: 'block', color: '#94A3B8', mt: 2, textAlign: 'center', lineHeight: 1.4 }}>
          Para não receber mais este aviso, finalize os tickets respondidos na Central de Suporte.
        </Typography>

        <Divider sx={{ mt: 2, mb: 0.5 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
          <Button
            size="small"
            onClick={handleDismiss}
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            Lembrar mais tarde
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => { handleDismiss(); window.open('/suporte', '_blank') }}
            sx={{
              bgcolor: '#475569', textTransform: 'none', fontWeight: 600,
              '&:hover': { bgcolor: '#334155' },
            }}
          >
            Ir para Central de Suporte
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default TicketPendingAlert
